import { useState, useEffect, useCallback } from 'react';
import { pwaService } from '../services/pwaService';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isStandalone: boolean;
  canInstall: boolean;
  installPrompt: BeforeInstallPromptEvent | null;
}

export const usePWA = () => {
  const [pwaState, setPwaState] = useState<PWAState>({
    isInstallable: false,
    isInstalled: false,
    isIOS: false,
    isAndroid: false,
    isStandalone: false,
    canInstall: false,
    installPrompt: null,
  });

  // 디바이스 및 설치 상태 감지
  useEffect(() => {
    const deviceInfo = pwaService.getDeviceInfo();
    
    setPwaState(prev => ({
      ...prev,
      isIOS: deviceInfo.isIOS,
      isAndroid: deviceInfo.isAndroid,
      isStandalone: deviceInfo.isStandalone,
      isInstalled: deviceInfo.isInstalled,
    }));
  }, []);

  // Install prompt 상태 감지
  useEffect(() => {
    const unsubscribe = pwaService.onInstallPromptChange((prompt) => {
      setPwaState(prev => ({
        ...prev,
        isInstallable: !!prompt,
        canInstall: !!prompt,
        installPrompt: prompt,
      }));
    });

    return unsubscribe;
  }, []);

  // 설치 함수
  const install = useCallback(async (): Promise<boolean> => {
    return await pwaService.install();
  }, []);

  // 서비스 워커 등록
  const registerServiceWorker = useCallback(async (): Promise<boolean> => {
    return await pwaService.registerServiceWorker();
  }, []);

  // 서비스 워커 업데이트 확인
  const checkForUpdates = useCallback(async (): Promise<boolean> => {
    return await pwaService.checkForUpdates();
  }, []);

  // 오프라인 상태 감지
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 푸시 알림 권한 요청
  const requestNotificationPermission = useCallback(async (): Promise<NotificationPermission> => {
    return await pwaService.requestNotificationPermission();
  }, []);

  // 푸시 알림 전송
  const sendNotification = useCallback((title: string, options?: NotificationOptions) => {
    pwaService.sendNotification(title, options);
  }, []);

  return {
    ...pwaState,
    isOnline,
    install,
    registerServiceWorker,
    checkForUpdates,
    requestNotificationPermission,
    sendNotification,
  };
};

